import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import css from './Step5Review.module.css';

const API_ERROR_MAP = {
  'email-taken': {
    message: 'This email address is already registered. Please use a different email.',
    hint: 'Go back to Step 2 and change the email address.',
    fixStep: 2,
    fixLabel: 'Edit Contact Info',
  },
  'email-invalid': {
    message: 'The email address you entered is not valid.',
    hint: 'Go back to Step 2 and enter a valid email.',
    fixStep: 2,
    fixLabel: 'Edit Contact Info',
  },
  'password-too-short': {
    message: 'Password is too short — minimum 8 characters.',
    hint: 'Go back to Step 2 and set a longer password.',
    fixStep: 2,
    fixLabel: 'Edit Contact Info',
  },
};

const getSignupErrorInfo = error => {
  if (!error) return null;
  const code = error?.apiErrors?.[0]?.code;
  const apiTitle = error?.apiErrors?.[0]?.title;
  if (code && API_ERROR_MAP[code]) return { ...API_ERROR_MAP[code], code };
  return {
    message: apiTitle || error?.message || 'Registration failed. Please review your information.',
    hint: null,
    fixStep: null,
    fixLabel: null,
    code,
  };
};

const Row = ({ label, value }) =>
  value ? (
    <div className={css.row}>
      <span className={css.label}>{label}</span>
      <span className={css.value}>{value}</span>
    </div>
  ) : null;

const Section = ({ title, stepNum, onEdit, children }) => (
  <div className={css.section}>
    <div className={css.sectionHeader}>
      <span className={css.sectionTitle}>{title}</span>
      <button type="button" className={css.editBtn} onClick={() => onEdit(stepNum)}>
        ✏ Edit
      </button>
    </div>
    <div className={css.sectionBody}>{children}</div>
  </div>
);

const AGREEMENT_LABELS = {
  agentAgreement1: 'ChinaDolphins Sourcing Agent Agreement',
  agentAgreement2: 'Privacy Policy',
  agentAgreement3: 'Agent Code of Conduct',
  agentAgreement4: 'Anti-Fraud & Compliance Declaration',
};

const AgentStep5Review = ({
  prevStep,
  goToStep,
  formData,
  onSubmit,
  submitInProgress,
  submitError,
  className,
  rootClassName,
}) => {
  const classes = classNames(rootClassName || css.root, className);
  const [confirmed, setConfirmed] = React.useState(false);
  const [confirmError, setConfirmError] = React.useState(false);

  const acceptedCount = Object.keys(AGREEMENT_LABELS).filter(k => formData[k]).length;

  const handleSubmit = e => {
    e.preventDefault();
    if (!confirmed) { setConfirmError(true); return; }
    setConfirmError(false);
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className={classNames(classes, css.form)}>

      <div className={css.header}>
        <span className={css.step}>Step 5 of 5</span>
        <h2 className={css.title}>Review & Submit</h2>
        <p className={css.description}>
          Review all your information below. Click <strong>Edit</strong> on any section to correct it.
        </p>
      </div>

      <hr className={css.divider} />

      {/* ── API error ── */}
      {submitError && (() => {
        const errInfo = getSignupErrorInfo(submitError);
        return (
          <div className={css.apiError}>
            <div className={css.apiErrorIcon}>⚠</div>
            <div className={css.apiErrorBody}>
              <strong>Registration failed.</strong>
              <p className={css.apiErrorMsg}>{errInfo.message}</p>
              {errInfo.hint && <p className={css.apiErrorHint}>{errInfo.hint}</p>}
              <div className={css.apiErrorActions}>
                {errInfo.fixStep ? (
                  <button type="button" className={css.goBackBtn} onClick={() => goToStep(errInfo.fixStep)}>
                    {errInfo.fixLabel}
                  </button>
                ) : (
                  <>
                    <button type="button" className={css.goBackBtn} onClick={() => goToStep(1)}>Edit Agent Info</button>
                    <button type="button" className={css.goBackBtn} onClick={() => goToStep(2)}>Edit Contact Info</button>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Section 1: Agent / Company ── */}
      <Section title="Agent / Company Information" stepNum={1} onEdit={goToStep}>
        <Row label="Company / Individual Name" value={formData.companyName} />
        <Row label="Registration No." value={formData.registrationNumber} />
        <Row label="Country" value={formData.country} />
        <Row label="City" value={formData.city} />
        <Row label="Description" value={formData.companyDescription} />
        {!formData.companyName && (
          <div className={css.missingWarning}>⚠ Agent information is incomplete — please edit.</div>
        )}
      </Section>

      {/* ── Section 2: Contact ── */}
      <Section title="Contact & Account" stepNum={2} onEdit={goToStep}>
        <Row label="Contact Person" value={formData.contactPersonName} />
        <Row label="Phone" value={formData.phone} />
        <Row label="Email" value={formData.email} />
        <Row
          label="Languages"
          value={Array.isArray(formData.languages) ? formData.languages.join(', ') : formData.languages}
        />
        {!formData.email && (
          <div className={css.missingWarning}>⚠ Contact information is incomplete — please edit.</div>
        )}
      </Section>

      {/* ── Section 3: Services ── */}
      <Section title="Services & Capabilities" stepNum={3} onEdit={goToStep}>
        <Row
          label="Industries Served"
          value={Array.isArray(formData.industriesServed) ? formData.industriesServed.join(', ') : formData.industriesServed}
        />
        <Row label="Years of Experience" value={formData.yearsOfExperience} />
        <Row label="Warehouse Availability" value={formData.warehouseAvailability ? '✓ Yes' : '✗ No'} />
        <Row label="Customs Assistance" value={formData.customsAssistance ? '✓ Yes' : '✗ No'} />
        <Row label="Sample Handling" value={formData.sampleHandling ? '✓ Yes' : '✗ No'} />
        <Row label="Door-to-Door Service" value={formData.doorToDoorService ? '✓ Yes' : '✗ No'} />
        {!formData.industriesServed?.length && (
          <div className={css.missingWarning}>⚠ No industries selected — please edit.</div>
        )}
      </Section>

      {/* ── Section 4: Agreements ── */}
      <Section title="Agreements" stepNum={4} onEdit={goToStep}>
        {Object.entries(AGREEMENT_LABELS).map(([key, label]) => (
          <div key={key} className={css.row}>
            <span className={css.label}>{label}</span>
            <span className={formData[key] ? css.green : css.red}>
              {formData[key] ? '✓ Accepted' : '✗ Not accepted'}
            </span>
          </div>
        ))}
        {acceptedCount < 4 && (
          <div className={css.missingWarning}>
            ⚠ {4 - acceptedCount} agreement(s) not accepted — please edit.
          </div>
        )}
      </Section>

      {/* ── Status notice ── */}
      <div style={{
        background: 'linear-gradient(135deg,#fefce8,#fef9c3)',
        border: '1px solid #fde68a',
        borderRadius: 12,
        padding: '14px 18px',
        marginBottom: 20,
        fontSize: 13,
        color: '#92400e',
        lineHeight: 1.6,
      }}>
        <strong>📋 Pending Approval</strong><br />
        After submitting, your application will be reviewed by the ChinaDolphins team.
        You will be notified by email once your account is approved, rejected, or if more information is needed.
      </div>

      {/* ── Confirm checkbox ── */}
      <label
        htmlFor="agentConfirmCheck"
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 14,
          padding: '18px 20px',
          borderRadius: 12,
          border: `1.5px solid ${confirmError ? '#ef4444' : confirmed ? '#2563eb' : '#e2e8f0'}`,
          background: confirmed
            ? 'linear-gradient(135deg,#eff6ff,#dbeafe)'
            : confirmError
            ? '#fff5f5'
            : '#f8fafc',
          cursor: 'pointer',
          marginBottom: 6,
          transition: 'all 0.2s',
          boxShadow: confirmed ? '0 2px 8px rgba(37,99,235,0.1)' : 'none',
        }}
      >
        {/* Custom checkbox */}
        <div style={{
          width: 22,
          height: 22,
          borderRadius: 6,
          border: `2px solid ${confirmed ? '#2563eb' : confirmError ? '#ef4444' : '#cbd5e1'}`,
          background: confirmed ? '#2563eb' : '#ffffff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          marginTop: 1,
          transition: 'all 0.18s',
        }}>
          {confirmed && (
            <svg width="13" height="10" viewBox="0 0 13 10" fill="none">
              <path d="M1.5 5L5 8.5L11.5 1.5" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )}
        </div>
        <input
          type="checkbox"
          id="agentConfirmCheck"
          checked={confirmed}
          onChange={e => { setConfirmed(e.target.checked); if (e.target.checked) setConfirmError(false); }}
          style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
        />
        <div style={{ fontSize: 14, lineHeight: 1.6, color: confirmed ? '#1d4ed8' : '#334155', fontWeight: confirmed ? 600 : 400 }}>
          I confirm that all the information provided is true and accurate, and I agree to operate
          in accordance with ChinaDolphins' agent guidelines.
          <span style={{ color: '#ef4444' }}> *</span>
        </div>
      </label>
      {confirmError && (
        <div style={{ fontSize: 12, color: '#ef4444', fontWeight: 600, marginBottom: 12, paddingLeft: 4 }}>
          ⚠ Please confirm that your information is accurate before submitting.
        </div>
      )}

      <div className={css.buttonContainer}>
        <button type="button" onClick={prevStep} className={css.previous}>← Previous</button>
        <button
          type="submit"
          className={classNames(css.submit, { [css.submitDisabled]: submitInProgress })}
          disabled={submitInProgress}
        >
          {submitInProgress ? 'Submitting…' : 'Submit Application →'}
        </button>
      </div>

    </form>
  );
};

AgentStep5Review.defaultProps = {
  className: null,
  rootClassName: null,
  formData: {},
  submitInProgress: false,
  submitError: null,
};

AgentStep5Review.propTypes = {
  className: PropTypes.string,
  rootClassName: PropTypes.string,
  formData: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  goToStep: PropTypes.func.isRequired,
  prevStep: PropTypes.func.isRequired,
  submitInProgress: PropTypes.bool,
  submitError: PropTypes.object,
};

export default AgentStep5Review;
